import * as React from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { getTool, TOOLS } from "@/content/tools";
import { SERVICES } from "@/content/services";
import { LOCATIONS } from "@/content/locations";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";

// Map tool category → most relevant Optimize Plus services. Powers the
// "Related services" cross-link strip on every tool detail page.
const TOOL_CATEGORY_TO_SERVICES: Record<string, string[]> = {
  "AI Search": ["aeo-geo", "content-strategy", "technical-seo"],
  "On-Page": ["content-strategy", "technical-seo", "cro"],
  Technical: ["technical-seo", "web-design", "analytics-reporting"],
  Content: ["content-strategy", "aeo-geo", "link-building"],
  Local: ["local-seo", "google-business-profile", "analytics-reporting"],
};
import {
  SerpPreviewTool, MetaLengthTool, ReadingGradeTool,
  GeoScoreTool, RobotsTesterTool, SchemaExplorerTool,
  OgPreviewTool, UtmBuilderTool, SlugGeneratorTool,
  HeadingOutlinerTool, KeywordDensityTool,
  LocalBusinessSchemaTool,

} from "@/components/tools/ToolWidgets";

export const Route = createFileRoute("/tools/$slug")({
  loader: ({ params }) => {
    const tool = getTool(params.slug);
    if (!tool) throw notFound();
    return { slug: tool.slug };
  },
  head: ({ loaderData, params }) => {
    const tool = loaderData ? getTool(loaderData.slug) : undefined;
    if (!tool) return { meta: [{ title: "Tool not found · Optimize Plus" }, { name: "robots", content: "noindex" }] };
    const title = `${tool.name} — free SEO tool · Optimize Plus`;
    const path = `/tools/${params.slug}/`;
    const base = pageMeta({
      path,
      title,
      description: tool.description,
      type: "website",
      keywords: [
        tool.name,
        `${tool.name} tool`,
        "free SEO tool",
        tool.category,
        "Austin SEO tools",
        "Optimize Plus",
      ],
    });
    const webApp = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: tool.name,
      description: tool.description,
      url: path,
      applicationCategory: "SEOApplication",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      operatingSystem: "Web",
      creator: { "@type": "Organization", name: "Optimize Plus", url: "/" },
    };
    const howToLd = tool.howTo?.length
      ? [{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: `How to use the ${tool.name}`,
          step: tool.howTo.map((s) => ({ "@type": "HowToStep", name: s.step, text: s.detail })),
        }]
      : [];
    const faqLdList = tool.faqs?.length ? [faqLd(tool.faqs)] : [];
    return {
      ...base,
      scripts: ldScripts(
        webApp,
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources/" },
          { name: "Tools", path: "/tools/" },
          { name: tool.name, path },
        ]),
        ...howToLd,
        ...faqLdList,
      ),
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-32">
      <h1 className="font-display text-4xl">Tool not found</h1>
      <Link to="/tools/" className="mt-4 inline-block text-primary">← Back to tools</Link>
    </div>
  ),
  component: ToolPage,
});

const WIDGETS: Record<string, () => React.ReactElement> = {
  "serp-preview": SerpPreviewTool,
  "meta-length-checker": MetaLengthTool,
  "reading-grade": ReadingGradeTool,
  "geo-score": GeoScoreTool,
  "robots-tester": RobotsTesterTool,
  "schema-explorer": SchemaExplorerTool,
  "og-preview": OgPreviewTool,
  "utm-builder": UtmBuilderTool,
  "slug-generator": SlugGeneratorTool,
  "heading-outliner": HeadingOutlinerTool,
  "keyword-density": KeywordDensityTool,
  "gmb-schema": LocalBusinessSchemaTool,
  "local-business-schema": LocalBusinessSchemaTool,
};

function ToolPage() {
  const { slug } = Route.useLoaderData();
  const tool = getTool(slug)!;
  const Widget = WIDGETS[tool.slug];
  const others = TOOLS.filter((t) => t.slug !== tool.slug).slice(0, 3);
  const relatedServiceSlugs = TOOL_CATEGORY_TO_SERVICES[tool.category] ?? [];
  const relatedServices = relatedServiceSlugs
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean) as (typeof SERVICES)[number][];
  const relatedLocations = LOCATIONS.slice(0, 6);

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <div className="container-page pt-8 text-xs uppercase tracking-widest text-muted-foreground">
          <Link to="/resources/" className="hover:text-foreground">Resources</Link>
          <span className="mx-2">/</span>
          <Link to="/tools/" className="hover:text-foreground">Tools</Link>
        </div>

        <section className="container-page pt-8 pb-12 max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <tool.icon className="h-7 w-7" aria-hidden />
            </span>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <span>{tool.category}</span>
              {tool.status === "beta" && <span className="rounded-full bg-primary/15 text-primary px-2 py-0.5">Beta</span>}
            </div>
          </div>
          <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">{tool.name}</h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{tool.tagline}</p>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{tool.description}</p>
        </section>

        <section className="container-page pb-16">
          <div className="rounded-3xl border border-border p-6 md:p-10 bg-card/30">
            {Widget ? <Widget /> : <p className="text-muted-foreground">Tool coming soon.</p>}
          </div>
        </section>

        {(tool.intro || tool.sections || tool.howTo || tool.faqs) && (
          <section className="container-page pb-16 max-w-3xl">
            {tool.intro && (
              <div className="space-y-5">
                {tool.intro.map((p, i) => (
                  <p key={i} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                ))}
              </div>
            )}

            {(tool.sections ?? []).map((s, i) => (
              <div key={i} className="mt-14">
                <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">{s.heading}</h2>
                <div className="mt-5 space-y-5">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                  ))}
                </div>
                {s.bullets && s.bullets.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 text-[1.02rem] leading-relaxed text-foreground/85">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {tool.howTo && tool.howTo.length > 0 && (
              <div className="mt-14">
                <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">Step-by-step workflow</h2>
                <ol className="mt-6 space-y-4">
                  {tool.howTo.map((s, i) => (
                    <li key={i} className="rounded-2xl border border-border p-5">
                      <div className="flex gap-3">
                        <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">{i + 1}</span>
                        <div>
                          <div className="font-display text-lg tracking-tight">{s.step}</div>
                          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.detail}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {tool.faqs && tool.faqs.length > 0 && (
              <div className="mt-16">
                <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">Frequently asked questions</h2>
                <dl className="mt-6 space-y-6">
                  {tool.faqs.map((f, i) => (
                    <div key={i} className="rounded-2xl border border-border p-6">
                      <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                      <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </section>
        )}

                <section className="container-page pb-16">
          <div className="rounded-2xl border border-border p-6">
            <div className="text-xs uppercase tracking-widest text-primary">Useful for</div>
            <ul className="mt-3 space-y-1.5 text-sm text-foreground/90">
              {tool.usefulFor.map((u) => <li key={u} className="flex gap-2"><span className="text-primary">·</span>{u}</li>)}
            </ul>
          </div>
        </section>

        <section className="container-page pb-32">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-6">Other tools</div>
          <div className="grid gap-6 md:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to="/tools/$slug/" params={{ slug: o.slug }} className="group rounded-2xl border border-border p-6 bg-card/30 hover:bg-card/50 hover:border-primary/40 transition-all">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20 mb-3">
                  <o.icon className="h-5 w-5" aria-hidden />
                </span>
                <EyebrowLabel>{o.category}</EyebrowLabel>
                <h4 className="mt-2 font-display text-xl leading-tight">{o.name}</h4>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{o.tagline}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Related services — orient tool users to paid engagements */}
        {relatedServices.length > 0 && (
          <section className="container-page pb-16" aria-labelledby="tool-svcs">
            <div className="rounded-3xl border border-border p-6 md:p-10 bg-card/30">
              <EyebrowLabel>Related Austin services</EyebrowLabel>
              <h2 id="tool-svcs" className="mt-3 font-display text-2xl md:text-3xl tracking-tight">
                Programmes that use this exact analysis.
              </h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {relatedServices.map((s) => (
                  <Link
                    key={s.slug}
                    to="/services/$slug/"
                    params={{ slug: s.slug } as any}
                    className="rounded-2xl border border-border p-5 bg-background hover:border-primary/40 transition-all"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">{s.category}</div>
                    <div className="mt-1 font-display text-lg">{s.title}</div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{s.metaDescription}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* City coverage — internal linking to location pages */}
        <section className="container-page pb-16" aria-labelledby="tool-cities">
          <EyebrowLabel>Austin & Texas coverage</EyebrowLabel>
          <h2 id="tool-cities" className="mt-3 font-display text-2xl md:text-3xl tracking-tight">
            Local SEO teams we work with.
          </h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {relatedLocations.map((l) => (
              <Link
                key={l.slug}
                    to={"/seo-agency-{$slug}" as any}
                    params={{ slug: l.slug } as any}
                className="surface-card p-4 hover:-translate-y-0.5 transition-transform"
              >
                <div className="text-[0.65rem] uppercase tracking-widest text-primary">{l.region}</div>
                <div className="mt-1 font-display text-base">SEO Agency — {l.city}, TX</div>
              </Link>
            ))}
          </div>
        </section>



        <section className="container-page pb-32">
          <div className="rounded-3xl border border-border p-10 bg-gradient-to-br from-primary/10 to-transparent">
            <EyebrowLabel>Need this at scale?</EyebrowLabel>
            <h3 className="mt-3 font-display text-3xl tracking-tight">We run this analysis across thousands of pages in engagements.</h3>
            <div className="mt-6"><ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="md">Start a project <span aria-hidden>→</span></ButtonLink></div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
